import React, { useState, useEffect, lazy, Suspense } from 'react';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { BudgetProvider, useBudget } from '@/contexts/BudgetContext';
import { SubscriptionProvider, useSubscription } from '@/contexts/SubscriptionContext';
import { RealtimeProvider } from '@/contexts/RealtimeContext';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { PreloadProvider } from '@/contexts/PreloadContext';
import { useRealtimeSync } from '@/hooks/useRealtimeSync';
import { Header } from '@/components/layout/Header';
import { MobileNav } from '@/components/layout/MobileNav';
import { Dashboard } from '@/pages/Dashboard';
import { LoginPage } from '@/pages/LoginPage';
import { TransactionList } from '@/components/transactions/TransactionList';
import { SettingsPanel } from '@/components/settings/SettingsPanel';
import { AddTransactionDialog } from '@/components/transactions/AddTransactionDialog';
import { ImportModals } from '@/components/imports/ImportModals';
import { CurrencyOnboarding } from '@/components/onboarding/CurrencyOnboarding';
import { TrialOffer } from '@/components/onboarding/TrialOffer';
import { UpgradeModal } from '@/components/subscription/UpgradeModal';
import { PWAInstallPrompt } from '@/components/pwa/PWAInstallPrompt';
import { LandingPage } from '@/pages/LandingPage';
import { Toaster } from '@/components/ui/sonner';
import { Loader2 } from 'lucide-react';

// Lazy load heavy pages
const Analytics = lazy(() => import('@/pages/Analytics').then(m => ({ default: m.Analytics })));
const Subscriptions = lazy(() => import('@/pages/Subscriptions').then(m => ({ default: m.Subscriptions })));
const ReceiptScanner = lazy(() => import('@/pages/ReceiptScanner').then(m => ({ default: m.ReceiptScanner })));
const AdminPage = lazy(() => import('@/pages/AdminPage').then(m => ({ default: m.AdminPage })));
const SavingsPage = lazy(() => import('@/pages/SavingsPage').then(m => ({ default: m.SavingsPage })));
const ProjectsPage = lazy(() => import('@/pages/ProjectsPage').then(m => ({ default: m.ProjectsPage })));
const SplitPage = lazy(() => import('@/pages/SplitPage').then(m => ({ default: m.SplitPage })));
const PricingPage = lazy(() => import('@/pages/PricingPage').then(m => ({ default: m.PricingPage })));
const SubscriptionSuccessPage = lazy(() => import('@/pages/PricingPage').then(m => ({ default: m.SubscriptionSuccessPage })));
const ScanPackSuccessPage = lazy(() => import('@/pages/PricingPage').then(m => ({ default: m.ScanPackSuccessPage })));
const TransactionPackSuccessPage = lazy(() => import('@/pages/PricingPage').then(m => ({ default: m.TransactionPackSuccessPage })));
const PowerPackSuccessPage = lazy(() => import('@/pages/PricingPage').then(m => ({ default: m.PowerPackSuccessPage })));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

const TABS = {
  dashboard: { title: 'SnapTally', subtitle: 'Track your finances' },
  transactions: { title: 'Transactions', subtitle: 'View all transactions' },
  subscriptions: { title: 'Subscriptions', subtitle: 'Recurring expenses' },
  projects: { title: 'Trips & Budgets', subtitle: 'Track trip & goal spending' },
  savings: { title: 'Savings', subtitle: 'Tips to save money' },
  analytics: { title: 'Analytics', subtitle: 'Insights & trends' },
  settings: { title: 'Settings', subtitle: 'Manage your preferences' },
  scanner: { title: 'Receipt Scanner', subtitle: 'AI expense extraction' },
  admin: { title: 'Admin', subtitle: 'Usage & costs' },
  pricing: { title: 'Pricing', subtitle: 'Choose your plan' },
};

// Loading screen component
const LoadingScreen = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-center">
      <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
);

// Auth callback handler for Google OAuth
// This component renders while AuthContext processes the session_id
const AuthCallback = () => {
  // The AuthContext will handle processing the session_id from the URL hash
  // This component just shows a loading state until that completes
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
};

// Main app content (protected)
const AppContent = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState(null);
  const [onboardingStep, setOnboardingStep] = useState('trial'); // 'trial' | 'currency' | 'done'
  const { isLoading: budgetLoading, needsOnboarding, completeOnboarding } = useBudget();
  const { hasFeature, isPremium, refreshSubscription } = useSubscription();

  // Enable real-time sync for transactions, settings, and budget alerts
  useRealtimeSync();

  const goToTransactions = () => setActiveTab('transactions');
  const goToDashboard = () => setActiveTab('dashboard');

  // Handle tab change - no feature gating, just entry limits
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Listen for navigation events from child components
  useEffect(() => {
    const handleNavigate = (e) => handleTabChange(e.detail);
    window.addEventListener('navigate', handleNavigate);
    return () => window.removeEventListener('navigate', handleNavigate);
  }, []);

  // Show onboarding flow for first-time users
  if (needsOnboarding) {
    if (onboardingStep === 'trial') {
      return (
        <TrialOffer 
          onStartTrial={() => {
            refreshSubscription();
            setOnboardingStep('currency');
          }}
          onSkip={() => setOnboardingStep('currency')}
        />
      );
    }
    return <CurrencyOnboarding onComplete={completeOnboarding} />;
  }

  // Check for subscription success/cancel routes
  const path = window.location.pathname;
  if (path === '/subscription/success') {
    return <Suspense fallback={<PageLoader />}><SubscriptionSuccessPage onContinue={() => {
      window.history.replaceState({}, '', '/');
      setActiveTab('dashboard');
      window.location.reload();
    }} /></Suspense>;
  }
  
  if (path === '/scan-pack/success') {
    return <Suspense fallback={<PageLoader />}><ScanPackSuccessPage onContinue={() => {
      window.history.replaceState({}, '', '/');
      setActiveTab('dashboard');
      window.location.reload();
    }} /></Suspense>;
  }

  if (path === '/transaction-pack/success') {
    return <Suspense fallback={<PageLoader />}><TransactionPackSuccessPage onContinue={() => {
      window.history.replaceState({}, '', '/');
      setActiveTab('dashboard');
      window.location.reload();
    }} /></Suspense>;
  }

  if (path === '/power-pack/success') {
    return <Suspense fallback={<PageLoader />}><PowerPackSuccessPage onContinue={() => {
      window.history.replaceState({}, '', '/');
      setActiveTab('dashboard');
      window.location.reload();
    }} /></Suspense>;
  }

  const renderContent = () => {
    if (budgetLoading) {
      return (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }
    
    if (activeTab === 'dashboard') return <Dashboard onViewAllTransactions={goToTransactions} />;
    if (activeTab === 'transactions') return <TransactionList />;
    if (activeTab === 'subscriptions') return <Suspense fallback={<PageLoader />}><Subscriptions /></Suspense>;
    if (activeTab === 'projects') return <Suspense fallback={<PageLoader />}><ProjectsPage /></Suspense>;
    if (activeTab === 'savings') return <Suspense fallback={<PageLoader />}><SavingsPage /></Suspense>;
    if (activeTab === 'split') return <Suspense fallback={<PageLoader />}><SplitPage /></Suspense>;
    if (activeTab === 'analytics') return <Suspense fallback={<PageLoader />}><Analytics /></Suspense>;
    if (activeTab === 'settings') return <SettingsPanel onOpenPricing={() => setActiveTab('pricing')} />;
    if (activeTab === 'scanner') return <Suspense fallback={<PageLoader />}><ReceiptScanner onBack={goToDashboard} /></Suspense>;
    if (activeTab === 'admin') return <Suspense fallback={<PageLoader />}><AdminPage /></Suspense>;
    if (activeTab === 'pricing') return <Suspense fallback={<PageLoader />}><PricingPage onBack={goToDashboard} /></Suspense>;
    return <Dashboard onViewAllTransactions={goToTransactions} />;
  };

  const currentTab = TABS[activeTab] || TABS.dashboard;
  const showNav = activeTab !== 'scanner';

  return (
    <div className="min-h-screen bg-background">
      {showNav && <Header title={currentTab.title} subtitle={currentTab.subtitle} onSettingsClick={() => setActiveTab('settings')} />}
      <main className="max-w-lg mx-auto px-4 py-4 pb-40">{renderContent()}</main>
      {showNav && (
        <MobileNav
          activeTab={activeTab}
          onTabChange={handleTabChange}
          onAddClick={() => setIsAddDialogOpen(true)}
        />
      )}
      <AddTransactionDialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen} />
      <ImportModals />
      <UpgradeModal 
        open={showUpgradeModal} 
        onOpenChange={setShowUpgradeModal}
        feature={upgradeFeature}
        onUpgrade={() => {
          setShowUpgradeModal(false);
          setActiveTab('pricing');
        }}
      />
      <PWAInstallPrompt />
      <Toaster position="top-center" toastOptions={{ style: { background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground))' } }} />
    </div>
  );
};

// Router component that handles auth state
const AppRouter = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const [showAuth, setShowAuth] = React.useState(false);

  // Check for privacy policy path (public page)
  if (window.location.pathname === '/privacy') {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <PrivacyPolicy />
      </Suspense>
    );
  }

  // Check for session_id in URL hash (Google OAuth callback)
  const hash = window.location.hash;
  if (hash && hash.includes('session_id=')) {
    return <AuthCallback />;
  }

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    // Show landing page by default, login page when user wants to sign in
    if (showAuth) {
      return <LoginPage onBack={() => setShowAuth(false)} />;
    }
    return <LandingPage onGetStarted={() => setShowAuth(true)} />;
  }

  return (
    <BudgetProvider>
      <SubscriptionProvider>
        <PreloadProvider>
          <RealtimeProvider>
            <NotificationProvider>
              <AppContent />
            </NotificationProvider>
          </RealtimeProvider>
        </PreloadProvider>
      </SubscriptionProvider>
    </BudgetProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppRouter />
    </AuthProvider>
  );
}

export default App;
